import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";

const testimonials = [
  {
    name: "Sarah M.",
    role: "Parent of Alex, age 9",
    text: "My son used to just play video games all day. Since joining Maple Code, he's actually building his own games! The confidence boost has been incredible.",
    rating: 5,
  },
  {
    name: "David K.",
    role: "Parent of Emma, age 7",
    text: "Emma absolutely loves her Intro to Coding class. She runs to the car every Tuesday excited to tell me what she built. The instructors are patient and engaging.",
    rating: 5,
  },
  {
    name: "Priya S.",
    role: "Parent of Arun, age 12",
    text: "The Python course has given Arun skills that are already helping him in school. He's problem-solving in ways I never expected. Worth every penny.",
    rating: 5,
  },
  {
    name: "Michael T.",
    role: "Parent of Lily, age 10",
    text: "Lily built a working robot in her first month! The STEM projects here are hands-on and practical. She's now talking about becoming an engineer.",
    rating: 5,
  },
  {
    name: "Jennifer L.",
    role: "Parent of Noah, age 8",
    text: "The small class sizes make such a difference. Noah gets personal attention and the teachers really understand how kids learn. Highly recommend!",
    rating: 5,
  },
  {
    name: "Robert C.",
    role: "Parent of Sophia, age 11",
    text: "Sophia's digital animation class has been transformative. She's creating incredible artwork and animations that blow our minds every week.",
    rating: 5,
  },
];

export const TestimonialsCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onSelect]);

  return (
    <section data-testid="testimonials-section" className="pt-[35px] pb-[35px] md:py-24 bg-[#EAF2FF]/50 noise-bg">
      <div className="mx-auto w-full max-w-[var(--container-max)] px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row items-start md:items-end justify-between mb-12 gap-6"
        >
          <div>
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold bg-[#EAF2FF] text-[#1677FF] mb-4">
              Testimonials
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              What{" "}
              <span className="text-[#1677FF]">Parents</span> Say
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={scrollPrev}
              disabled={!canScrollPrev}
              className="rounded-full w-10 h-10 border-border"
              data-testid="testimonials-prev-button"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={scrollNext}
              disabled={!canScrollNext}
              className="rounded-full w-10 h-10 border-border"
              data-testid="testimonials-next-button"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </motion.div>

        {/* Carousel */}
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex ml-[-24px]">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="flex-[0_0_100%] min-w-0 pl-6 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
                data-testid={`testimonial-card-${i}`}
              >
                <Card className="relative bg-white p-6 rounded-2xl border border-border shadow-[var(--shadow-soft)] speech-bubble h-full">
                  <Quote className="w-8 h-8 text-[#1677FF]/15 mb-4" />
                  <p className="text-sm leading-relaxed text-foreground/80 mb-6">
                    "{t.text}"
                  </p>
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, si) => (
                      <Star key={si} className="w-4 h-4 fill-[#F7D154] text-[#F7D154]" />
                    ))}
                  </div>
                  <div>
                    <p className="text-sm font-bold">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
